import { navigate } from 'hookrouter';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { KNOWN_ERROR_CODES } from '../../../api/constants';
import { KnownError } from '../../../api/errors';
import { activateAuthenticator } from '../../../api/fetch/activate-authenticator';
import { enrollAuthenticator } from '../../../api/fetch/enroll-authenticator';
import { ActivateAccountMessage } from '../../../components/ui/ActivateAccountMessage';
import { PageLayout } from '../../../components/ui/PageLayout';
import { SMSVerification } from '../../../components/ui/SMSVerification';
import { WelcomeMGMRewards } from '../../../components/ui/WelcomeMGMRewards';
import { usePasskeyEnabled } from '../../../hooks';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { useContent } from '../../../hooks/useContent';
import { useCountdown } from '../../../hooks/useCountdown';
import { useInput } from '../../../hooks/useInput';
import { useLoadingLabel } from '../../../hooks/useLoadingLabel';
import { useEvent } from '../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../utils/analytics/usePageLoadEvent';
import {
  defaultProfile2FAPath,
  IDW_FLOW_PARAM,
  IDENTITY_WIDGET_PATHS,
  MFA_ENROLL_STATUS_PARAM,
} from '../../../utils/constants';
import { getPersistedParameters } from '../../../utils/persistParameterContext';
import { useUserAccount } from '../../../utils/userAccountContext';
import { MfaEnrollmentFlowType } from '../Enrollment/types';

// Session storage keys for cross-page-reload enrollment deduplication
// This prevents duplicate enroll API calls when Adobe Launch/DTM scripts cause
// the page to fully remount in production (triggering 429 rate limits)
const MFA_SMS_ENROLLMENT_ID_KEY = 'mfa_sms_enrollment_id';
const MFA_SMS_ENROLLMENT_TS_KEY = 'mfa_sms_enrollment_ts';
const MFA_SMS_ENROLLMENT_LOCK_KEY = 'mfa_sms_enrollment_lock';
const ENROLLMENT_TTL_MS = 30000; // 30 seconds - enrollment is considered fresh within this window
const LOCK_TTL_MS = 15000; // 15 seconds - lock expires if API call hangs/fails without cleanup

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function SMSVerificationPage({ type }: { type: 'enrollment' | 'authentication' }) {
  const redirectToApp = useAppRedirect();
  const { getContentByPath } = useContent();
  const trackEvent = useEvent();
  const { passkeyEnabled } = usePasskeyEnabled();
  const eventNameError = getContentByPath<string>('mfa.analytics.error.eventName');
  const trackSuffixError = getContentByPath<string>('mfa.analytics.error.trackSuffix');
  usePageLoadEvent('identity', '2fa setup: sms auth code');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setLoadingState] = useState(false);
  const submitCTA = useLoadingLabel(isLoading, 'SMSVerificationPage.submit');
  const {
    bind: bindVerificationCode,
    value: verificationCode,
    reset: resetVerificationCode,
  } = useInput('', 'verificationCode');
  const [authenticatorActivated, setAuthenticatorActivated] = useState('');
  const [inputError, setInputError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [codeResentSuccessfully, setCodeResentSuccessfully] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [showWelcomeMgmRewardsScreen, setShowWelcomeMgmRewardsScreen] = useState(false);
  const [showAccountActivatedScreen, setShowAccountActivatedScreen] = useState(false);
  const params = getPersistedParameters();
  const idwFlow = params?.[IDW_FLOW_PARAM] as MfaEnrollmentFlowType;

  const countDown = useCountdown(30);

  const [isEnrolled, setIsEnrolled] = useState(false);
  const { phoneNumber } = useUserAccount();

  const enrollmentAttempted = useRef(false);
  const isEnrolledRef = useRef(false);

  const enrollSMS = useCallback(
    async formattedNumber => {
      try {
        if (isEnrolledRef.current || enrollmentAttempted.current) {
          return;
        }
        enrollmentAttempted.current = true;
        const payload = {
          type: 'sms',
          phoneNumber: formattedNumber,
        };

        // enrollAuthenticator extracts user ID from the JWT token internally,
        // so we don't need to gate on mgmId from context (which may not be populated in sign-in flow)
        const response = await enrollAuthenticator(payload);
        setOpenAlert(true);
        setCodeResentSuccessfully(true);
        setAuthenticatorActivated(response.id ?? '');
        if (response) {
          isEnrolledRef.current = true;
          setIsEnrolled(true);
          countDown.startCountdown();

          // Persist enrollment ID in sessionStorage to prevent duplicate calls
          // on page remount (caused by Adobe Launch/DTM scripts in production)
          try {
            sessionStorage.setItem(MFA_SMS_ENROLLMENT_ID_KEY, response.id ?? '');
            sessionStorage.setItem(MFA_SMS_ENROLLMENT_TS_KEY, String(Date.now()));
          } catch (e) {
            // sessionStorage may be unavailable in some contexts; non-critical
          }

          const message = getContentByPath('mfa.sms.enterPhoneNumber.codeResentSuccess') as string;
          await trackEvent(
            'identity – unified MFA notification',
            message,
            'unifiedmfanotification',
            { notificationDisplayed: message },
            true,
          );
        } else {
          setErrorMessage(getContentByPath('mfa.sms.enrollFailed'));
        }
      } catch (err) {
        let message;
        if (err instanceof KnownError) {
          if (err.code === 'E041') {
            message = KNOWN_ERROR_CODES.E041;
          }
        } else {
          message = getContentByPath('MFA.systemError');
        }
        setOpenAlert(true);
        setErrorMessage(message);
        trackEvent(eventNameError, message, trackSuffixError, {}, true);
        enrollmentAttempted.current = false;

        // Clear the lock on failure so retry/resend can work
        try {
          sessionStorage.removeItem(MFA_SMS_ENROLLMENT_LOCK_KEY);
        } catch (e) {
          // non-critical
        }
      }
    },
    [getContentByPath, countDown, trackEvent, eventNameError, trackSuffixError],
  );

  useEffect(() => {
    if (phoneNumber && type === 'enrollment' && !enrollmentAttempted.current && !isEnrolled) {
      // Check sessionStorage for a recent enrollment OR an in-flight lock to prevent
      // duplicate API calls. In production, Adobe Launch/DTM scripts can cause a full
      // page remount which re-triggers this useEffect. The sessionStorage check deduplicates.
      try {
        // First check: completed enrollment (has ID + timestamp)
        const storedEnrollmentId = sessionStorage.getItem(MFA_SMS_ENROLLMENT_ID_KEY);
        const storedTimestamp = sessionStorage.getItem(MFA_SMS_ENROLLMENT_TS_KEY);

        if (storedEnrollmentId && storedTimestamp) {
          const elapsed = Date.now() - parseInt(storedTimestamp, 10);
          if (elapsed < ENROLLMENT_TTL_MS) {
            // Enrollment was already done recently — recover state without making API call
            enrollmentAttempted.current = true;
            isEnrolledRef.current = true;
            setIsEnrolled(true);
            setAuthenticatorActivated(storedEnrollmentId);
            setOpenAlert(true);
            setCodeResentSuccessfully(true);
            countDown.startCountdown();
            return;
          }
        }

        // Second check: in-flight lock (API call started but not yet completed)
        const lockTimestamp = sessionStorage.getItem(MFA_SMS_ENROLLMENT_LOCK_KEY);
        if (lockTimestamp) {
          const lockElapsed = Date.now() - parseInt(lockTimestamp, 10);
          if (lockElapsed < LOCK_TTL_MS) {
            // Another instance already started the enrollment — skip to avoid 429
            enrollmentAttempted.current = true;
            return;
          }
        }
      } catch (e) {
        // sessionStorage unavailable — fall through to normal enrollment
      }

      // Set an optimistic lock BEFORE making the API call
      // This prevents race conditions where multiple instances mount simultaneously
      try {
        sessionStorage.setItem(MFA_SMS_ENROLLMENT_LOCK_KEY, String(Date.now()));
      } catch (e) {
        // non-critical
      }

      enrollSMS(phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber, type]);

  const validateCode = useCallback(() => {
    setTouched(true);
    if (verificationCode.length === 0) {
      setInputError(getContentByPath('mfa.sms.fields.verificationCode.required') || 'Verification code is required');
      return false;
    }
    if (verificationCode.length > 0 && verificationCode.length < 6) {
      setInputError(getContentByPath('mfa.sms.fields.verificationCode.invalid') || 'Invalid code');
      trackEvent(
        eventNameError,
        getContentByPath('mfa.sms.fields.verificationCode.invalid') || 'Invalid code',
        trackSuffixError,
        {},
        true,
      );
      return false;
    }
    setInputError('');
    return true;
  }, [verificationCode, getContentByPath, trackEvent, eventNameError, trackSuffixError]);

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: numericValue },
    };
    bindVerificationCode.onChange(syntheticEvent);

    if (touched) {
      if (numericValue.length === 6) {
        setInputError('');
      } else if (numericValue.length > 0 && numericValue.length < 6) {
        setInputError(getContentByPath('mfa.sms.fields.verificationCode.invalid') || 'Invalid code');
      }
    }
  };

  const submitVerificationCode = useCallback(async () => {
    await trackEvent('identity – unified MFA CTAs', 'Submit Code - SMS authentication', 'unifiedmfactas', {}, true);

    try {
      const payload = { passCode: verificationCode };
      const authenticatorId = authenticatorActivated;

      if (!authenticatorId) {
        throw new Error('Enrollment not completed. Please try again.');
      }

      // activateAuthenticator extracts user ID from the JWT token internally,
      // so we don't need to gate on mgmId from context
      const response = await activateAuthenticator(authenticatorId, payload);
      console.log('sms authenticator activated successfully', response);

      await trackEvent(
        'identity – unified MFA notification',
        'SMS authentication has been added',
        'unifiedmfanotification',
        { notificationDisplayed: 'SMS authentication has been added' },
        true,
      );
    } catch (err) {
      setCodeResentSuccessfully(false);

      const errorField = 'Verification Code';
      let errorDisplayed = 'The provided passcode is incorrect or has expired';

      if (err instanceof KnownError) {
        const errorCode = err.code;
        switch (errorCode) {
          case 'E008':
            setInputError(KNOWN_ERROR_CODES.E0000068);
            setOpenAlert(true);
            errorDisplayed = KNOWN_ERROR_CODES.E0000068;
            break;

          case 'E34':
            setErrorMessage(KNOWN_ERROR_CODES.E34);
            setOpenAlert(true);
            errorDisplayed = KNOWN_ERROR_CODES.E34;
            break;

          case 'E042':
            setErrorMessage(KNOWN_ERROR_CODES.E042);
            setOpenAlert(true);
            errorDisplayed = KNOWN_ERROR_CODES.E042;
            break;

          case 'E054':
            setErrorMessage(KNOWN_ERROR_CODES.E054);
            setOpenAlert(true);
            errorDisplayed = KNOWN_ERROR_CODES.E054;
            break;

          case 'E043':
            setErrorMessage(KNOWN_ERROR_CODES.E043);
            setOpenAlert(true);
            errorDisplayed = KNOWN_ERROR_CODES.E043;
            break;

          default:
            setErrorMessage(getContentByPath('mfa.sms.systemError'));
            errorDisplayed = 'System Error';
            break;
        }
      } else {
        setErrorMessage(getContentByPath('mfa.sms.systemError'));
        errorDisplayed = 'System Error';
      }

      await trackEvent(eventNameError, errorDisplayed, trackSuffixError, { errorField, errorDisplayed }, true);
      throw err;
    }
  }, [verificationCode, authenticatorActivated, trackEvent, getContentByPath, eventNameError, trackSuffixError]);

  const handleSubmit = useCallback(async () => {
    if (!validateCode()) {
      return;
    }
    setLoadingState(true);
    try {
      await submitVerificationCode();
      let queryParams = {};
      if (type === 'enrollment') {
        // If passkey is enabled and this is a signup/activate flow, navigate to passkey enrollment post-2FA
        if (passkeyEnabled && (idwFlow === 'signup' || idwFlow === 'activate')) {
          navigate(IDENTITY_WIDGET_PATHS.MfaEnrollPasskey, true, { [IDW_FLOW_PARAM]: idwFlow });
          return;
        } else if (idwFlow === 'signin') {
          // For sign-in flow, redirect immediately after successful enrollment
          redirectToApp({}, true);
          return;
        } else {
          if (idwFlow === 'activate') {
            // Display activation success screen
            setShowAccountActivatedScreen(true);
          } else if (idwFlow === 'signup') {
            setShowWelcomeMgmRewardsScreen(true);
          }
        }

        if (
          document?.referrer?.includes(defaultProfile2FAPath) ||
          params?.redirect_uri?.includes(defaultProfile2FAPath)
        ) {
          queryParams = { [MFA_ENROLL_STATUS_PARAM]: '2fa-sms-added' };
        }
      }
      // Add 2-second delay before redirecting to app
      setTimeout(() => {
        redirectToApp(queryParams, true);
      }, 2000);
    } catch (err) {
      setLoadingState(false);
      console.error('Error during sms authentication:', err);
    }
  }, [
    validateCode,
    submitVerificationCode,
    redirectToApp,
    type,
    setShowWelcomeMgmRewardsScreen,
    setShowAccountActivatedScreen,
    idwFlow,
    params?.redirect_uri,
    passkeyEnabled,
  ]);

  const handleResendCode = async () => {
    if (!countDown.isRunning) {
      try {
        // Clear any previous error states
        setErrorMessage('');
        setInputError('');
        setTouched(false);
        resetVerificationCode();
        enrollmentAttempted.current = false;
        isEnrolledRef.current = false;
        setIsEnrolled(false);

        // Clear cached enrollment and lock so a fresh enroll call can be made
        try {
          sessionStorage.removeItem(MFA_SMS_ENROLLMENT_ID_KEY);
          sessionStorage.removeItem(MFA_SMS_ENROLLMENT_TS_KEY);
          sessionStorage.removeItem(MFA_SMS_ENROLLMENT_LOCK_KEY);
        } catch (e) {
          // non-critical
        }

        await trackEvent('identity – unified MFA CTAs', 'Resend Code - SMS authentication', 'unifiedmfactas', {}, true);
        await enrollSMS(phoneNumber);
        setCodeResentSuccessfully(true);
        countDown.startCountdown();
      } catch (err) {
        setCodeResentSuccessfully(false);
        setOpenAlert(true);
        setErrorMessage(getContentByPath('mfa.sms.codeResentFail'));
        trackEvent(eventNameError, getContentByPath('mfa.sms.codeResentFail'), trackSuffixError, {}, true);
      }
    }
  };

  const maskPhoneNumber = phone => {
    const cleaned = phone.replace(/[^\d+]/g, ''); // remove spaces, dashes etc.

    const match = cleaned.match(/^(\+\d{1,3})?(.*?)(\d{4})$/);

    if (!match) return phone;

    const countryCode = match[1] || '';
    const last4 = match[3];

    return `${countryCode}... ...${last4}`;
  };

  const onCloseAlert = () => {
    setOpenAlert(false);
    setErrorMessage('');
  };
  const handleVerificationCodeBlur = () => {
    validateCode();
  };
  return (
    <div data-testid='route-/mfa/enroll/sms-verify'>
      {showAccountActivatedScreen ? (
        <PageLayout>
          <ActivateAccountMessage />
        </PageLayout>
      ) : showWelcomeMgmRewardsScreen ? (
        <PageLayout>
          <WelcomeMGMRewards />
        </PageLayout>
      ) : (
        <SMSVerification
          onCloseAlert={onCloseAlert}
          phoneNumber={phoneNumber ? maskPhoneNumber(phoneNumber) : 'unknown'}
          verificationCode={verificationCode}
          onVerificationCodeChange={handleVerificationCodeChange}
          onVerificationCodeBlur={handleVerificationCodeBlur}
          onSubmit={handleSubmit}
          onResend={handleResendCode}
          isLoading={isLoading}
          inputError={!!inputError}
          inputHint={inputError}
          isCountdownRunning={countDown.isRunning}
          secondsLeft={countDown.secondsLeft}
          submitButtonLabel={submitCTA || getContentByPath('mfa.sms.submit')}
          codeResentSuccessfully={codeResentSuccessfully}
          errorMessage={errorMessage}
          openAlert={openAlert}
          type={type}
        />
      )}
    </div>
  );
}

SMSVerificationPage.displayName = 'SMSVerificationPage';
